import React, { useState, useRef } from "react"
import {
  View,
  TouchableOpacity,
  ScrollView,
  Modal,
  TouchableWithoutFeedback,
} from "react-native"
import { Body, H4, H6 } from "@/components/standard/BFText"
import CaretDown from "phosphor-react-native/src/icons/CaretDown"
import { cn } from "@/components/util/cn"
import { type DropdownOption } from "@/types/dropdownTypes"
import Spacer from "@/components/util/Spacer"

interface BFDropDownProps<T extends string | boolean> {
  options: Array<DropdownOption<T>>
  value: T | null
  onValueChange?: (value: T | undefined) => void
  placeholder?: string
  readonlyText?: string
  testID?: string
  zIndex: number
  fullWidth?: boolean
  maxHeight?: number // Determines if scrolling is enabled
  disabled?: boolean
}

const BFDropDown = <T extends string | boolean>({
  options = [],
  value,
  onValueChange,
  placeholder = "Select...",
  readonlyText = "No change",
  testID = "bf-dropdown",
  zIndex = 1,
  fullWidth = false,
  maxHeight,
  disabled,
}: BFDropDownProps<T>) => {
  const [isOpen, setIsOpen] = useState(false)
  const [dropdownLayout, setDropdownLayout] = useState({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  })
  const dropdownRef = useRef<View>(null)

  // Determine the selected label based on the exact lower-cased match
  const selectedLabel = options.find((option) => {
    if (value === null || value === undefined) return false
    if (typeof value === "boolean") return option.value === value
    return String(option.value) === String(value)
  })?.label

  const handleToggle = () => {
    if (!onValueChange) return
    if (dropdownRef.current) {
      dropdownRef.current.measureInWindow((x, y, width, height) => {
        setDropdownLayout({ x, y, width, height })
        setIsOpen(!isOpen)
      })
    }
  }

  const handleSelect = (selectedValue: T) => {
    onValueChange?.(selectedValue)
    setIsOpen(false)
  }

  const optionsWidth = fullWidth ? dropdownLayout.width : 260

  return (
    <View style={{ zIndex }}>
      <TouchableOpacity
        ref={dropdownRef}
        testID={`${testID}-toggle`}
        activeOpacity={onValueChange ? 0.6 : 1}
        onPress={handleToggle}
        className={cn(
          "rounded-md flex-row items-center bg-lightest",
          onValueChange
            ? isOpen
              ? "border border-calm-blue"
              : "border border-light-grey"
            : "" // No border if onValueChange is undefined
        )}
        style={{
          width: fullWidth ? "100%" : 144,
          minWidth: 170,
          height: 50,
          paddingHorizontal: 10,
          justifyContent: "space-between",
          alignItems: onValueChange ? "center" : "flex-start", // Align top for readonly text only
          flexDirection: "row",
        }}
        disabled={disabled}
      >
        {onValueChange ? (
          <>
            <Body
              className={cn(
                "text-[14px] truncate",
                selectedLabel === undefined
                  ? "text-warm-grey"
                  : "text-midnight-blue"
              )}
            >
              {selectedLabel || placeholder}
            </Body>
            <Spacer />
            {!disabled && <CaretDown size={16} color="#000" />}
          </>
        ) : (
          <H4 className="text-midnight-blue items-start text-right flex-1">
            {readonlyText}
          </H4>
        )}
      </TouchableOpacity>

      {isOpen && onValueChange && (
        <Modal
          visible={isOpen}
          transparent={true}
          animationType="none"
          onRequestClose={() => {
            setIsOpen(false)
          }}
        >
          <TouchableWithoutFeedback
            onPress={() => {
              setIsOpen(false)
            }}
          >
            <View style={{ flex: 1 }}>
              <TouchableWithoutFeedback>
                <View
                  style={{
                    position: "absolute",
                    top: dropdownLayout.y + dropdownLayout.height + 4,
                    left: fullWidth
                      ? dropdownLayout.x
                      : dropdownLayout.x + dropdownLayout.width - optionsWidth,
                    width: optionsWidth,
                    borderWidth: 1,
                    borderColor: "#dcdcdc",
                    borderRadius: 4,
                    backgroundColor: "#fff",
                    elevation: 2,
                    shadowColor: "#000",
                    shadowOffset: { width: 1, height: 1 },
                    shadowOpacity: 0.1,
                    shadowRadius: 1.2,
                  }}
                  className="border border-light-grey rounded-md bg-lightest"
                >
                  <ScrollView
                    nestedScrollEnabled={true}
                    style={maxHeight ? { maxHeight } : {}}
                  >
                    {options.map((item, index) => (
                      <TouchableOpacity
                        key={index}
                        testID={`${testID}-option-${String(item.value)}`}
                        activeOpacity={0.1}
                        onPress={() => {
                          handleSelect(item.value)
                        }}
                        className={cn(
                          value === item.value
                            ? "bg-[#B2C7D499]"
                            : "bg-lightest",
                          "px-4 py-3 border-b border-light-grey last:border-b-0"
                        )}
                      >
                        <H6
                          className={cn(
                            value === item.value
                              ? "text-calm-blue"
                              : "text-dark-grey"
                          )}
                        >
                          {item.label}
                        </H6>
                      </TouchableOpacity>
                    ))}
                  </ScrollView>
                </View>
              </TouchableWithoutFeedback>
            </View>
          </TouchableWithoutFeedback>
        </Modal>
      )}
    </View>
  )
}

export default BFDropDown
